import type { NumericalQuestionT } from '@mentimeter/question-modules-types';
import type { GuessTheNumberStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { NumericalQuestionPresentation } from './NumericalQuestionPresentation';

// We have the same type for controls, copy pasted for now
// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SlideStateNumericalQuestion = { showCorrect?: boolean };

export function NumericalQuestionPresentationDataHandler() {
  const {
    useResults,
    useSlideState,
    useConfig,
    usePace,
    InteractiveCanvasComponents,
  } = usePresentationContext();

  const slide = usePresentationSlideContext();
  const { results = [] } = useResults<NumericalQuestionT>();
  const [{ showCorrect }] = useSlideState<SlideStateNumericalQuestion>();
  const { currentStep } = usePace();

  const { mode, enableAnimations } = useConfig();

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldShowCorrect =
    showCorrect ||
    mode === 'idle' ||
    mode === 'screenshot' ||
    visualizationState === 'view-correct-answer';

  const interactiveContent = slide.interactiveContents[0]!;
  const staticContent = slide.staticContent as GuessTheNumberStaticContent;

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  return (
    <NumericalQuestionPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      responseRange={{
        min: interactiveContent.responseRange?.min ?? 0,
        max: interactiveContent.responseRange?.max ?? 10,
      }}
      responseRangeScale={
        staticContent.interactiveContentStyling.responseRangeScale
      }
      correctAnswerRange={{
        min: interactiveContent.choices[0]!.correctAnswerRange?.min ?? 0,
        max: interactiveContent.choices[0]!.correctAnswerRange?.max ?? 1,
      }}
      results={results[0]?.score}
      shouldShowCorrect={shouldShowCorrect}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      shouldAnimate={enableAnimations}
      shouldHideResults={shouldHideResults}
    />
  );
}
